import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Car, Bike, Home, Building2, ArrowRight, CheckCircle } from 'lucide-react';
import { heroImages } from '../mock';

const HomePage = () => {
  const navigate = useNavigate();
  const [currentServiceIndex, setCurrentServiceIndex] = useState(0);

  const services = [
    { name: 'Car', path: '/car', icon: Car, color: '#156DD1' },
    { name: 'Bike', path: '/bike', icon: Bike, color: '#0171A8' },
   /* { name: 'Appliance', path: '/appliance', icon: Home, color: '#1458A4' },
    { name: 'PG or Accommodation', path: '/pg', icon: Building2, color: '#156DD1' } */
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentServiceIndex((prev) => (prev + 1) % services.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [services.length]);

  const currentService = services[currentServiceIndex];
  const ServiceIcon = currentService.icon;

  return (
    <div className="min-h-screen">
      {/* Hero Section with Animation */}
      <section className="hero-gradient min-h-[90vh] flex items-center relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Animated Text */}
            <div className="text-white space-y-8">
              <div className="space-y-4">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                  Want Things
                  <br />
                  <span className="text-white/90">On Rent?</span>
                </h1>
                
                <div className="h-24 flex items-center">
                  <div
                    key={currentServiceIndex}
                    className="animate-service-fade text-4xl md:text-5xl font-bold flex items-center gap-4"
                    style={{ color: currentService.color }}
                  >
                    <ServiceIcon size={48} className="drop-shadow-lg" />
                    <span className="text-white drop-shadow-lg">{currentService.name}</span>
                  </div>
                </div>
              </div>

              <p className="text-xl text-white/90 max-w-lg">
                Your one-stop solution for all rental needs. Quality products, flexible terms, and hassle-free service.
              </p>

              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => navigate(currentService.path)}
                  className="px-8 py-4 bg-white text-primary rounded-lg hover:bg-white/90 transition-all font-semibold text-lg flex items-center gap-2 shadow-xl hover:scale-105 transform"
                >
                  Explore {currentService.name}
                  <ArrowRight size={20} />
                </button>
                <button
                  onClick={() => navigate('/cart')}
                  className="px-8 py-4 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-all font-semibold text-lg border border-white/30"
                >
                  View Cart
                </button>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-8">
                <div className="text-center">
                  <div className="text-3xl font-bold">500+</div>
                  <div className="text-white/80">Products</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">10K+</div>
                  <div className="text-white/80">Happy Customers</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">24/7</div>
                  <div className="text-white/80">Support</div>
                </div>
              </div>
            </div>

            {/* Right Side - Animated Image */}
            <div className="relative hidden lg:block">
              {heroImages.map((item, index) => (
                <div
                  key={index}
                  className={`transition-all duration-1000 ${
                    index === currentServiceIndex
                      ? 'opacity-100 scale-100 translate-x-0'
                      : 'opacity-0 scale-95 translate-x-10 absolute inset-0'
                  }`}
                >
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                    <img
                      src={item.image}
                      alt={item.service}
                      className="w-full h-[600px] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-20 right-20 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
      </section>

      {/* Services Grid Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose from our wide range of rental services tailored to meet your needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  onClick={() => navigate(service.path)}
                  className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2"
                  style={{
                    borderTop: `4px solid ${service.color}`
                  }}
                >
                  <div
                    className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: `${service.color}20` }}
                  >
                    <Icon size={32} style={{ color: service.color }} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {service.name === 'PG or Accommodation' ? 'PG/Accommodation' : service.name}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Explore our range of {service.name.toLowerCase()} rentals with flexible plans
                  </p>
                  <div className="flex items-center gap-2 text-primary font-semibold group-hover:gap-4 transition-all">
                    Browse Now <ArrowRight size={20} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Rentzo?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We make renting easy, affordable, and hassle-free
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Flexible Plans',
                description: 'Choose from daily, weekly, or monthly rental plans that suit your needs'
              },
              {
                title: 'Quality Products',
                description: 'Well-maintained and regularly serviced products for your peace of mind'
              },
              {
                title: 'Quick Delivery',
                description: 'Fast and reliable delivery right to your doorstep'
              },
              {
                title: 'Easy Returns',
                description: 'Hassle-free return process with no hidden charges'
              },
              {
                title: '24/7 Support',
                description: 'Round-the-clock customer support to assist you anytime'
              },
              {
                title: 'Affordable Prices',
                description: 'Competitive pricing with transparent billing and no surprises'
              }
            ].map((feature, index) => (
              <div key={index} className="flex gap-4">
                <div className="flex-shrink-0">
                  <CheckCircle size={28} className="text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-gradient py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Renting?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Browse our catalog and find the perfect rental solution for you
          </p>
          <button
            onClick={() => navigate('/car')}
            className="px-10 py-4 bg-white text-primary rounded-lg hover:bg-white/90 transition-all font-semibold text-lg inline-flex items-center gap-2 shadow-xl hover:scale-105 transform"
          >
            Get Started Now
            <ArrowRight size={20} />
          </button>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
